import { beEmailMessageApiSearchMessages } from "Api/beEmailMessageApi";
import {
  beFspDeleteImportFile,
  beFspUploadImportFile,
  type FspFileNames,
  type FspResp,
  type FspFileResp,
  beFspGetImportFiles,
  type GetImportFilesResp,
  type FspGetImportFilesParams,
  type FspFileObj,
  beFspDownloadImportFile,
  beFspDownloadImportExampleFile,
} from "Api/beFsp";
import { beRecipientGetDynamicFieldsDefinition } from "Api/beRecipientApi";
import {
  beRecipientApiGetDefaultMapping,
  beRecipientApiPreviewImportFile,
  beRecipientApiSaveImportConfiguration,
  beRecipientApiCopyPasteExtractor,
} from "Api/beRecipientApiTS";
import { STORE_KEY_ID_LIST } from "Constants";

import { storeGet } from "mailup-react-helpers";
import type {
  GetDefaultMappingResponse,
  ImportFilesConfigFieldMapping,
  ImportFilesConfigRequest,
  ImportFilesConfigResponse,
  ImportFilesPreviewRequest,
  ImportFilesPreviewResponse,
} from "Types/beRecipientsApi";

import type { MessageTypeEnum } from "Types/beEmailMessageApi";
import { beMessApiGetListActiveMessage } from "Api/beMessApi";

export const uploadImportFile = async (fileObj: File): Promise<FspFileResp> => {
  const idList = storeGet(STORE_KEY_ID_LIST);
  const content = await fileToBase64(fileObj);
  const ret = await beFspUploadImportFile(idList, {
    FileName: fileObj.name,
    FileContent: content,
  });

  return ret;
};

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (reader.result && typeof reader.result === "string") {
        // Rimuove il prefisso "data:*/*;base64," per ottenere solo il contenuto base64
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      } else {
        reject(new Error("Failed to read file as data URL"));
      }
    };
    reader.onerror = (error) => reject(error);
  });
};

export const deleteImportFile = async (
  data: FspFileNames,
): Promise<FspResp> => {
  const idList = storeGet(STORE_KEY_ID_LIST);
  const ret = await beFspDeleteImportFile(idList, data);

  return ret;
};

export const getImportFiles = async (
  qsParams: FspGetImportFilesParams,
): Promise<GetImportFilesResp> => {
  const idList = storeGet(STORE_KEY_ID_LIST);
  const ret = await beFspGetImportFiles(idList, qsParams);

  return ret;
};

export const downloadImportFile = async (
  fileName: string,
): Promise<FspFileObj> => {
  const idList = storeGet(STORE_KEY_ID_LIST);
  return await beFspDownloadImportFile(idList, fileName);
};

export const downloadImportExampleFile = async (): Promise<FspFileObj> => {
  return await beFspDownloadImportExampleFile();
};

export const importFilesPreview = async (
  fileName: string,
  req: ImportFilesPreviewRequest,
): Promise<ImportFilesPreviewResponse> => {
  const idList = storeGet(STORE_KEY_ID_LIST);

  const ret = await beRecipientApiPreviewImportFile(idList, fileName, req);
  if (!ret) {
    return {
      Rows: [],
      ShowXlsWarning: false,
    };
  }
  return ret;
};

export const getDynamicFields = async () => {
  const res = await beRecipientGetDynamicFieldsDefinition();

  return res;
};

export const getConfirmationMessages = async (): Promise<{
  items: Array<{ label: string; value: number }>;
}> => {
  const idList = storeGet(STORE_KEY_ID_LIST);

  const { Items, TotalItems } = await beEmailMessageApiSearchMessages(
    idList,
    {
      pageNumber: 0,
      pageSize: 0,
      orderBy: "Id",
      orderDescending: true,
    },
    {
      ServiceTypes: [0, 3],
      MessageTypes: ["Confirmation"],
    },
  );

  return {
    items: Items.filter((f) => f.Archived === false).map(({ Id, Subject }) => ({
      value: Id,
      label: `${Id} - ${Subject}`,
    })),
    //totalItems: TotalItems,
  };
};

export const fetchDefaultConfirmationMesssage = async (): Promise<number> => {
  const idList: number = storeGet(STORE_KEY_ID_LIST);

  const result = await beMessApiGetListActiveMessage(idList, "Confirmation");

  return result;
};

export const saveImportConfiguration = async (
  fileName: string,
  req: ImportFilesConfigRequest,
): Promise<ImportFilesConfigResponse> => {
  const idList: number = storeGet(STORE_KEY_ID_LIST);

  const result = await beRecipientApiSaveImportConfiguration(
    idList,
    fileName,
    req,
  );

  return result;
};

export const getDefaultMapping =
  async (): Promise<GetDefaultMappingResponse> => {
    const idList: number = storeGet(STORE_KEY_ID_LIST);

    const result = await beRecipientApiGetDefaultMapping(idList);
    return result;
  };

export const extractCopyPaste = async (
  text: string,
  type: string,
): Promise<string[]> => {
  const idList: number = storeGet(STORE_KEY_ID_LIST);

  const result = await beRecipientApiCopyPasteExtractor(idList, {
    Text: text,
    Type: type,
  });
  return result.Items;
};
